import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import './Products.css';
import './ProductsActions.css';
import './AllSellStyles.css';
import './MobileResponsive.css';

import { formatDateTime, formatDate } from '../../utils/dateUtils';

const statusMap = {
  ordered: { label: 'Ordered', className: 'badge badge-info' },
  partial: { label: 'Partial', className: 'badge badge-warning' },
  completed: { label: 'Completed', className: 'badge badge-success' },
};

const PurchaseRequisition = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    location_id: '',
    status: '',
    start_date: '',
    end_date: '',
    required_by_start: '',
    required_by_end: '',
  });
  const [locations, setLocations] = useState([]);

  const [viewItem, setViewItem] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (rowId, event) => {
    event.stopPropagation();
    if (openActionId === rowId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(rowId);
    }
  };

  const fetchData = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        page,
        per_page: perPage,
        search: searchTerm,
      };
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.status) params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.required_by_start) params.required_by_start = filters.required_by_start;
      if (filters.required_by_end) params.required_by_end = filters.required_by_end;

      const result = await apiService.getPurchaseRequisitions(params);
      if (result.success) {
        setData(result.data || []);
        setTotal(result.total || 0);
        setCurrentPage(result.current_page || 1);
        setLastPage(result.last_page || 1);
      } else {
        setError(result.message || 'Failed to load purchase requisitions');
      }
    } catch (err) {
      setError(err.message || 'Failed to load purchase requisitions');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await apiService.getLocations();
      const list = Array.isArray(res) ? res : (res.data || []);
      setLocations(list);
    } catch (err) {
      /* console.error('Error fetching locations:', err); */
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchData(1);
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [perPage, filters]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(1), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      location_id: '',
      status: '',
      start_date: '',
      end_date: '',
      required_by_start: '',
      required_by_end: '',
    });
    setSearchTerm('');
  };

  const handleExport = (format) => {
    if (data.length === 0) {
      alert('No data available to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Requisition No', 'Date', 'Delivery Date', 'Requested By', 'Location', 'Status'];
    const rows = data.map((r) => [
      r.ref_no || '',
      formatDate(r.transaction_date),
      formatDate(r.delivery_date),
      r.added_by || '',
      r.location_name || '',
      r.status || '',
    ]);

    const title = 'Purchase Requisitions';
    const filename = 'purchase_requisitions';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleDelete = async (id) => {
    setOpenActionId(null);
    if (!window.confirm('Are you sure you want to delete this purchase requisition?')) return;
    try {
      const result = await apiService.deletePurchaseRequisition(id);
      if (result && result.success !== false) {
        toast.success('Purchase requisition deleted successfully.');
        fetchData(currentPage);
      } else {
        toast.error(result?.message || 'Failed to delete purchase requisition');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase requisition');
    }
  };

  const handleView = async (id) => {
    setOpenActionId(null);
    setViewLoading(true);
    setViewItem(null);
    try {
      const result = await apiService.getPurchaseRequisition(id);
      if (result && result.success !== false) {
        setViewItem(result.data || result);
      } else {
        toast.error(result?.message || 'Failed to load requisition details');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load requisition details');
    } finally {
      setViewLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    fetchData(page);
  };

  return (
    <div className="products-container">
      <div className="main-card">

        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => navigate('/purchase_requisition/add')}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {isLoading ? (
          <div className="loading-sales">
            <h3>⏳ Loading Purchase Requisitions...</h3>
            <p>Fetching purchase requisitions data from server...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="no-sales">
            <h3>📊 No Purchase Requisitions Found</h3>
            <p>No purchase requisitions have been recorded yet.</p>
          </div>
        ) : (
          <>
            <div className="products-table-container">
              <table className="products-table fit-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Date</th>
                    <th>Reference No</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Required by date</th>
                    <th>Added By</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.id}>
                      <td className="actions-cell">
                        <div className="action-dropdown-container">
                          <button className="action-btn-trigger" onClick={(e) => toggleDropdown(row.id, e)}>
                            Actions <span className="arrow-down">▼</span>
                          </button>
                          {openActionId === row.id && ReactDOM.createPortal(
                            <div
                              className="action-dropdown-menu"
                              style={{
                                position: 'fixed',
                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                left: `${dropdownPosition.left}px`,
                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                zIndex: 9999999
                              }}
                            >
                              <button className="dropdown-item" onClick={() => handleView(row.id)}>👁️ View</button>
                              <button className="dropdown-item delete-item" onClick={() => handleDelete(row.id)}>🗑️ Delete</button>
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td>{formatDateTime(row.transaction_date || row.created_at)}</td>
                      <td>{row.ref_no || '--'}</td>
                      <td>{row.location_name || row.location?.name || '--'}</td>
                      <td>
                        <span className="payment-status-badge" style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.26rem 0.72rem',
                          borderRadius: '20px',
                          fontSize: '0.78rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          color: '#ffffff',
                          letterSpacing: '0.2px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          background: row.status === 'completed' ? 'linear-gradient(135deg, #10b981, #059669)' : row.status === 'partial' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #0284c7, #0369a1)'
                        }}>
                          {row.status ? row.status.toUpperCase() : 'ORDERED'}
                        </span>
                      </td>
                      <td>{row.delivery_date || row.required_by_date ? formatDateTime(row.delivery_date || row.required_by_date) : '--'}</td>
                      <td>{row.added_by || row.requested_by || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
              <div>
                Showing {total === 0 ? 0 : ((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                  Page {currentPage} of {lastPage}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Filter Modal (Popup) */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '92%', background: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filter Purchase Requisitions
              </h3>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>

            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="filter-group">
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem', display: 'block' }}>Location:</label>
                  <select name="location_id" value={filters.location_id} onChange={handleFilterChange} style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }}>
                    <option value="">All Locations</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem', display: 'block' }}>Status:</label>
                  <select name="status" value={filters.status} onChange={handleFilterChange} style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }}>
                    <option value="">All Statuses</option>
                    <option value="ordered">Ordered</option>
                    <option value="partial">Partial</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem', display: 'block' }}>Date From:</label>
                  <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }} />
                </div>

                <div className="filter-group">
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem', display: 'block' }}>Date To:</label>
                  <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }} />
                </div>

                <div className="filter-group">
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem', display: 'block' }}>Required By From:</label>
                  <input type="date" name="required_by_start" value={filters.required_by_start} onChange={handleFilterChange} style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }} />
                </div>

                <div className="filter-group">
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem', display: 'block' }}>Required By To:</label>
                  <input type="date" name="required_by_end" value={filters.required_by_end} onChange={handleFilterChange} style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }} />
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <button
                type="button"
                onClick={clearFilters}
                style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '600', color: '#64748b', cursor: 'pointer' }}
              >
                Reset Filters
              </button>
              <button
                type="button"
                onClick={() => {
                  fetchData(1);
                  setShowFilters(false);
                }}
                className="control-btn-primary"
                style={{ padding: '0.5rem 1.25rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}
              >
                Apply & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {viewItem && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setViewItem(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ background: '#ffffff', width: '90%', maxWidth: '1150px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '8px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', padding: 0 }}>
            {/* Modal Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Purchase requisition details (Reference No: #{viewItem.ref_no || ''})
              </h2>
              <button onClick={() => setViewItem(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {viewLoading ? (
                <div style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b', fontSize: '1rem' }}>⏳ Loading requisition details...</div>
              ) : (
                <>
                  {/* Top 3-Column Section */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 600 }}>Requested By:</div>
                      <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                        {viewItem.added_by || viewItem.requested_by || viewItem.created_by_user?.username || 'Admin'}
                      </div>
                    </div>

                    <div>
                      <div style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 600 }}>Business Location:</div>
                      <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                        {viewItem.location?.name || viewItem.location_name || '-'}
                      </div>
                      {viewItem.location?.landmark || viewItem.location?.address_line_1 ? (
                        <div style={{ color: '#475569', marginTop: '2px' }}>
                          {viewItem.location.landmark || viewItem.location.address_line_1}
                        </div>
                      ) : null}
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#475569', marginBottom: '2px' }}><strong>Requisition No:</strong> #{viewItem.ref_no || '-'}</div>
                      <div style={{ color: '#475569', marginBottom: '2px' }}><strong>Date:</strong> {formatDate(viewItem.transaction_date)}</div>
                      <div style={{ color: '#475569', marginBottom: '2px' }}><strong>Required By Date:</strong> {formatDate(viewItem.delivery_date)}</div>
                      <div>
                        <strong>Status: </strong>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '12px',
                          fontSize: '0.78rem',
                          fontWeight: '600',
                          background: viewItem.status === 'completed' ? '#dcfce7' : viewItem.status === 'partial' ? '#fef3c7' : '#e0f2fe',
                          color: viewItem.status === 'completed' ? '#15803d' : viewItem.status === 'partial' ? '#b45309' : '#0369a1',
                          border: `1px solid ${viewItem.status === 'completed' ? '#bbf7d0' : viewItem.status === 'partial' ? '#fde68a' : '#bae6fd'}`,
                          textTransform: 'capitalize'
                        }}>
                          {viewItem.status ? viewItem.status.charAt(0).toUpperCase() + viewItem.status.slice(1) : 'Ordered'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Green Header Products Table */}
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>Products</h4>
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#22c55e', color: '#ffffff', textAlign: 'left' }}>
                          <th style={{ padding: '0.65rem 0.75rem', width: '5%' }}>#</th>
                          <th style={{ padding: '0.65rem 0.75rem' }}>Product Name</th>
                          <th style={{ padding: '0.65rem 0.75rem', width: '25%' }}>SKU</th>
                          <th style={{ padding: '0.65rem 0.75rem', width: '20%' }}>Quantity</th>
                          <th style={{ padding: '0.65rem 0.75rem', width: '20%' }}>Secondary Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!viewItem.purchase_lines || viewItem.purchase_lines.length === 0 ? (
                          <tr><td colSpan={5} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>No products found</td></tr>
                        ) : (
                          viewItem.purchase_lines.map((line, idx) => (
                            <tr key={line.id || idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                              <td style={{ padding: '0.65rem 0.75rem', color: '#64748b' }}>{idx + 1}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontWeight: 600, color: '#1e293b' }}>
                                {line.product?.name || line.variations?.product_variation?.name || `Variation #${line.variation_id}`}
                              </td>
                              <td style={{ padding: '0.65rem 0.75rem', color: '#475569' }}>
                                {line.product?.sku || line.variations?.sub_sku || '-'}
                              </td>
                              <td style={{ padding: '0.65rem 0.75rem', fontWeight: 500 }}>
                                {line.quantity} Single
                              </td>
                              <td style={{ padding: '0.65rem 0.75rem', color: '#64748b' }}>
                                {line.secondary_unit_quantity || '-'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
              <button
                onClick={() => setViewItem(null)}
                style={{ background: '#334155', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PurchaseRequisition;
